import { useState } from 'react';
import toast from 'react-hot-toast';

import { useData } from '../../utils/hooks/useData';
import { useDynamicEntityStoreContext, useDynamicEntityCMSDataContext } from '../contexts';

import TextInput from '../../inputs/form-inputs/TextInput';
import LocaleOption from './_alternates_pane/LocaleOption';
import ConfirmationDialog from '../../confirmation-dialog/ConfirmationDialog';

const sortByGroupKey = (a, b) => {
  return a.group_key.localeCompare(b.group_key);
};

export default function AlternatesPane() {
  const { siteLocales, endpoints } = useDynamicEntityCMSDataContext();

  const updateData = useDynamicEntityStoreContext((state) => state.updateData);

  const assignedHreflangGroup = useDynamicEntityStoreContext((state) => state.data.assigned_hreflang_group);
  const assignedHreflangGroupId = useDynamicEntityStoreContext((state) => state.data.assigned_hreflang_group.hreflang_group_id);
  const assignedHreflangGroupLocale = useDynamicEntityStoreContext((state) => state.data.assigned_hreflang_group.locale);

  const { hreflangGroupsUrl, hreflangGroupUrl, assignHreflangGroupAssignmentsUrl, unassignHreflangGroupAssignmentsUrl } = endpoints;

  const [groupKey, setGroupKey] = useState('');
  const [groupToDelete, setGroupToDelete] = useState(null);

  if (siteLocales === undefined || siteLocales === null) {
    return (
      <div className="h-100 w-100 d-flex flex-column gap-3 align-items-center justify-content-center">
        <span className="text-muted">No locales are set up yet.</span>
        <span className="text-muted">Please set up your locales in the site settings page.</span>
      </div>
    );
  }

  const {
    data: hrefLangGroups,
    isLoading: isLoadingHreflangGroups,
    setData: setHreflangGroups,
  } = useData(`${hreflangGroupsUrl}.json`, siteLocales !== undefined, null, 'hreflang_groups');

  const locales = siteLocales.split('|');

  const handleAddNewGroup = async () => {
    try {
      const response = await fetch(`${hreflangGroupsUrl}.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ hreflang_group: { group_key: groupKey } }),
      });

      if (response.ok) {
        const data = await response.json();

        if (data.error) throw new Error(data.error);

        setHreflangGroups((prevGroups) => {
          if (prevGroups === null) return [data.hreflang_group];

          // Add the new data.hreflang_group to the existing array and then sort it alphabetically
          const newGroups = [...prevGroups, data.hreflang_group];
          return newGroups.sort((a, b) => sortByGroupKey(a, b));
        });

        setGroupKey('');

        toast.success(`Created group key '${groupKey}'!`);
      } else {
        const res = await response.json();
        if (res.error.details && res.error.details.length > 0) {
          throw new Error(JSON.stringify(res.error.details.join(' ')));
        } else {
          throw new Error('Failed to create group with the provided key.');
        }
      }
    } catch (error) {
      toast.error(error.toString());
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;

    const { id, group_key } = groupToDelete;

    try {
      const response = await fetch(`${hreflangGroupUrl.replace(':id', id)}.json`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (response.ok) {
        setHreflangGroups((prevGroups) => prevGroups.filter((group) => group.id !== id));
        toast.success(`Deleted group key: '${group_key}'!`);
      } else {
        const data = await response.json();
        throw new Error(data?.error?.details);
      }
    } catch (error) {
      toast.error(error.toString());
    }
  };

  const handleAssignment = async (hreflangGroup, locale, hreflangGroupAssignmentId) => {
    const { id, group_key } = hreflangGroup;

    try {
      const response = await fetch(`${assignHreflangGroupAssignmentsUrl}.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ hreflang_group_assignment: { hreflang_group_id: id, locale, id: hreflangGroupAssignmentId } }),
      });

      if (response.ok) {
        const data = await response.json();

        if (data.error) throw new Error(data.error);

        const { assigned_hreflang_group, removed_hreflang_group_assignment } = data;

        updateData({ assigned_hreflang_group });

        setHreflangGroups((prevGroups) => {
          return prevGroups.map((group) => {
            if (group.id === id) {
              if (removed_hreflang_group_assignment) {
                group.hreflang_group_assignments = group.hreflang_group_assignments.filter(
                  (assignment) => assignment.id !== removed_hreflang_group_assignment.id,
                );
              }

              return {
                ...group,
                hreflang_group_assignments: [...(group.hreflang_group_assignments ?? []), assigned_hreflang_group],
              };
            }
            return group;
          });
        });

        toast.success(`Assigned to Locale: ${locale} | Group: ${group_key} !`);
      } else {
        throw new Error(`Failed to assign this page to the locale "${locale}" under the group "${group_key}"`);
      }
    } catch (error) {
      toast.error(error.toString());
    }
  };

  const handleUnassignment = async (hreflangGroup, hreflangGroupAssignmentId) => {
    const { id } = hreflangGroup;
    try {
      const response = await fetch(`${unassignHreflangGroupAssignmentsUrl}.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ id: hreflangGroupAssignmentId, hreflang_group_id: id }),
      });

      if (response.ok) {
        const data = await response.json();

        if (data.error) throw new Error(data.error);

        const { unassigned_hreflang_group } = data;
        const { hreflang_group_id, id, locale } = unassigned_hreflang_group;

        if (hreflang_group_id === assignedHreflangGroupId && locale === assignedHreflangGroupLocale) {
          updateData({
            assigned_hreflang_group: { hreflang_group_id: null, hreflang_group_key: null, id: null, locale: null },
          });
        }

        setHreflangGroups((prevGroups) => {
          return prevGroups.map((group) => {
            if (group.id === hreflang_group_id) {
              return {
                ...group,
                hreflang_group_assignments: group.hreflang_group_assignments.filter((assignment) => assignment.id !== id),
              };
            }
            return group;
          });
        });

        toast.success(`Page unassigned from the locale ${unassigned_hreflang_group.locale} under the group ${hreflangGroup.group_key} successfully!`);
      } else {
        throw new Error('Failed to unassign this page from the locale');
      }
    } catch (error) {
      toast.error(error.toString());
    }
  };

  const filteredGroups = hrefLangGroups
    ? groupKey
      ? hrefLangGroups.filter((hlg) => hlg.group_key.toLowerCase().includes(groupKey.toLowerCase()))
      : hrefLangGroups
    : [];

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex flex-column">
        <h3>Edit Alternates</h3>
        <p className="text-muted mb-0">Manage your builder page alternates here.</p>
      </div>
      <div className="border rounded p-3">
        <div className="d-flex gap-2 text-muted mb-0">
          <i className="fe fe-info" />
          <span className=""> You can assign and unassign pages here without needing to click 'Save'.</span>
        </div>
      </div>
      <div className="d-flex flex-column gap-3">
        <div className="d-flex flex-column justify-content-center gap-4">
          <div className="d-flex flex-column gap-2">
            <TextInput label="Group Key" placeholder="Enter group key to search or add..." value={groupKey} onChange={(e) => setGroupKey(e.target.value)} />
            {!isLoadingHreflangGroups && groupKey !== '' && (
              <button className="btn btn-primary" onClick={handleAddNewGroup}>
                Add New Group (+)
              </button>
            )}
          </div>
          {isLoadingHreflangGroups ? (
            <div className="d-flex justify-content-center align-items-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100">
              {filteredGroups.length > 0 ? (
                <div className="w-100 h-100">
                  <div className="accordion" id="alternatesAccordion">
                    {filteredGroups
                      .sort((a, b) => sortByGroupKey(a, b))
                      .map((hreflang_group) => {
                        const { id, group_key, hreflang_group_assignments } = hreflang_group;

                        const accordionId = `accordion-${id}`;
                        const isAssigned = assignedHreflangGroupId === id;

                        return (
                          <div key={accordionId} className="accordion-item">
                            <h2 className="accordion-header">
                              <button
                                className="accordion-button"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#${accordionId}`}
                                aria-controls={accordionId}
                              >
                                <div className="d-flex gap-2">
                                  {group_key} {isAssigned && <span className="badge bg-primary">This page</span>}
                                </div>
                              </button>
                            </h2>
                            <div id={accordionId} className="accordion-collapse collapse" data-bs-parent="#alternatesAccordion">
                              <div className="accordion-body">
                                <div className="d-flex flex-column gap-3">
                                  <div className="d-flex flex-column gap-3">
                                    {locales.map((locale) => {
                                      const assigned = hreflang_group_assignments?.find((assignment) => assignment.locale === locale);
                                      const currentAssigned = assignedHreflangGroup;

                                      return (
                                        <LocaleOption
                                          key={locale}
                                          {...{
                                            hreflangGroupId: id,
                                            locale,
                                            assigned,
                                            handleAssignment: () => handleAssignment(hreflang_group, locale, currentAssigned.id),
                                            handleUnassignment: () => handleUnassignment(hreflang_group, assigned?.id),
                                          }}
                                        />
                                      );
                                    })}
                                  </div>
                                  <div className="d-flex justify-content-end ">
                                    <button
                                      className="btn btn-sm btn-danger"
                                      data-bs-toggle="modal"
                                      data-bs-target="#deleteGroupKeyAlternates-builder"
                                      onClick={() => {
                                        setGroupToDelete({ id, group_key });
                                      }}
                                    >
                                      Delete Group
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ) : (
                <div>No groups found.</div>
              )}
            </div>
          )}
        </div>
      </div>
      <ConfirmationDialog
        id="deleteGroupKeyAlternates-builder"
        title="Confirm Delete Group"
        message={`Are you sure you wish to ${groupToDelete?.group_key}?`}
        confirmButtonText="Delete"
        confirmButtonClass="btn-danger"
        onConfirm={handleDeleteGroup}
      />
    </div>
  );
}
